import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import styles from './UIGameLog.module.scss';
import clsx from 'clsx';
import { createFeedMessageDataForGameLogData } from '../UIGameFeedMessage/FeedMessageDataLogs.ts';
import { UIGameFeedMessage } from '../UIGameFeedMessage/UIGameFeedMessage.tsx';
import { cloneDeep } from 'lodash-es';
import { UIGameFeedVirtualScrollContainer } from '../UIGameFeedVirtualScrollContainer/UIGameFeedVirtualScrollContainer.tsx';
import { usePlayerValidators } from '../../../store/selectors/usePlayerValidators.ts';
import { useGameStore } from '../../../store/GameStoreContext.ts';
import { useIsReplay } from '../../../store/selectors/useIsReplay.ts';
const UIGameLogFeedMessage = /*#__PURE__*/ memo(function UIGameLogFeedMessage({ playerValidator, gameValidator, gameLogData, opacity = 1, onClick, logIndex }) {
    const translationKeyContext = useGameStore((state)=>state.gameMode.translationKeyContext);
    const feedMessageData = createFeedMessageDataForGameLogData(gameValidator, gameLogData, translationKeyContext, playerValidator);
    return /*#__PURE__*/ React.createElement(UIGameFeedMessage, {
        message: feedMessageData,
        onClick: onClick,
        opacity: opacity,
        logIndex: logIndex
    });
}, (prev, next)=>prev.opacity === next.opacity);
// TODO remove once UIReplayController is converted to ReactJS
let globalAddNewLog;
export function addNewLog(logIndex, gameLogData) {
    if (globalAddNewLog) globalAddNewLog(logIndex, gameLogData);
}
export function UIGameLog({ isLogVisible, onClickLog, gameValidator, backgroundColor }) {
    const gameLogState = useGameStore((state)=>state.gameState.gameLogState);
    const isReplay = useIsReplay();
    const currentPlayerPerspective = useGameStore((state)=>{
        var _state_gameReplay_replayControls;
        return (_state_gameReplay_replayControls = state.gameReplay.replayControls) === null || _state_gameReplay_replayControls === void 0 ? void 0 : _state_gameReplay_replayControls.currentPlayerPerspective;
    });
    const focusLogIndex = useGameStore((state)=>state.gameReplay.focusedLogIndex);
    const { getPlayerValidator } = usePlayerValidators();
    const [logs, setLogs] = useState({});
    globalAddNewLog = (logIndex, gameLogData)=>{
        const newLogData = {
            data: gameLogData
        };
        if (gameLogData.from != null) {
            const playerValidator = getPlayerValidator(gameLogData.from);
            // User's avatar can be bot avatar or their actual avatar. cloneDeep to keep the avatar at the state of that particular log
            newLogData.playerValidator = cloneDeep(playerValidator);
        }
        setLogs((prevLogs)=>({
                ...prevLogs,
                [logIndex]: newLogData
            }));
    };
    // Initial logs from gameState when UIGame is initialized in UIWGameManager
    useEffect(()=>{
        if (gameLogState != null) {
            const logsFromState = {};
            for(const key in gameLogState){
                const logData = gameLogState[key];
                const logItem = {
                    data: logData
                };
                if (logData.from != null) {
                    const playerValidator = getPlayerValidator(logData.from);
                    logItem.playerValidator = cloneDeep(playerValidator);
                }
                logsFromState[key] = logItem;
            }
            setLogs(logsFromState);
        }
    }, [
        currentPlayerPerspective
    ]);
    const logIndexToChildIndex = useRef(new Map());
    const messages = useMemo(()=>{
        logIndexToChildIndex.current.clear();
        const msgs = [];
        for(const key in logs){
            const logIndex = Number(key);
            const gameLogData = logs[key].data;
            const playerValidator = logs[key].playerValidator;
            const opacity = focusLogIndex !== null && logIndex > focusLogIndex ? 0.3 : 1;
            const message = /*#__PURE__*/ React.createElement(UIGameLogFeedMessage, {
                gameLogData: gameLogData,
                gameValidator: gameValidator,
                key: key,
                logIndex: logIndex,
                onClick: onClickLog,
                opacity: opacity,
                playerValidator: playerValidator
            });
            logIndexToChildIndex.current.set(logIndex, msgs.length);
            msgs.push(message);
        }
        return msgs;
    }, [
        logs,
        focusLogIndex,
        onClickLog
    ]);
    const autoScrollToBottom = !isReplay;
    const scrollToChildIndex = useMemo(()=>{
        if (autoScrollToBottom) return undefined;
        if (focusLogIndex === null) return 0;
        const childIndex = logIndexToChildIndex.current.get(focusLogIndex);
        if (childIndex === undefined) return 0;
        return childIndex;
    }, [
        focusLogIndex,
        currentPlayerPerspective,
        autoScrollToBottom
    ]);
    if (!isLogVisible) return null;
    return /*#__PURE__*/ React.createElement("div", {
        className: clsx(styles.container, styles[backgroundColor])
    }, /*#__PURE__*/ React.createElement(UIGameFeedVirtualScrollContainer, {
        autoScrollToBottom: autoScrollToBottom,
        scrollToChildIndex: scrollToChildIndex,
        suppressAutoScrollOnUserScroll: true
    }, messages));
}
